import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { AxiosRequestConfig } from "axios";
import { useAuthStore } from "@/stores/auth/auth-store";

export type ReAuthReason = 'session_expired' | 'token_invalid' | 'refresh_failed' | null;

interface PendingRequest {
  config: AxiosRequestConfig;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

interface ReAuthState {
  // State
  isOpen: boolean;
  reason: ReAuthReason;
  pendingRequests: PendingRequest[];
  isReAuthenticating: boolean;
  lastEmail: string | null;

  // Actions
  openModal: (reason: ReAuthReason, email?: string) => void;
  closeModal: () => void;
  setReAuthenticating: (value: boolean) => void;
  addPendingRequest: (request: PendingRequest) => void;
  resolvePendingRequests: () => void;
  rejectPendingRequests: (error: Error) => void;
  clearPendingRequests: () => void;
  reset: () => void;
}

type ReAuthStoreStateSlice = Pick<
  ReAuthState,
  "isOpen" | "reason" | "pendingRequests" | "isReAuthenticating" | "lastEmail"
>;

const initialState: ReAuthStoreStateSlice = {
  isOpen: false,
  reason: null,
  pendingRequests: [],
  isReAuthenticating: false,
  lastEmail: null,
};

const hasRecoverableAuthSession = (): boolean => {
  const { isAuthenticated, tokens, user } = useAuthStore.getState();
  return isAuthenticated || Boolean(tokens?.accessToken) || Boolean(user);
};

export const useReAuthStore = create<ReAuthState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      ...initialState,

      openModal: (reason: ReAuthReason, email?: string) => {
        if (!hasRecoverableAuthSession()) return;

        const state = get();
        // Don't open if already open
        if (state.isOpen) return;

        set((state) => {
          state.isOpen = true;
          state.reason = reason;
          if (email) {
            state.lastEmail = email;
          }
        });
      },

      closeModal: () => {
        set((state) => {
          state.isOpen = false;
          state.reason = null;
          state.isReAuthenticating = false;
        });
      },

      setReAuthenticating: (value: boolean) => {
        set((state) => {
          state.isReAuthenticating = value;
        });
      },

      addPendingRequest: (request: PendingRequest) => {
        set((state) => {
          state.pendingRequests.push(request);
        });
      },

      resolvePendingRequests: () => {
        const { pendingRequests } = get();
        // Resolve all pending requests - they will be retried with new token
        pendingRequests.forEach(({ resolve }) => {
          resolve('retry');
        });
        set((state) => {
          state.pendingRequests = [];
        });
      },

      rejectPendingRequests: (error: Error) => {
        const { pendingRequests } = get();
        // Reject all pending requests
        pendingRequests.forEach(({ reject }) => {
          reject(error);
        });
        set((state) => {
          state.pendingRequests = [];
        });
      },

      clearPendingRequests: () => {
        set((state) => {
          state.pendingRequests = [];
        });
      },

      reset: () => {
        set(() => ({ ...initialState }));
      },
    })),
  ),
);

// Selector hooks for common use cases
export const useReAuthModal = () => {
  const { isOpen, reason, isReAuthenticating, lastEmail, openModal, closeModal, setReAuthenticating } = useReAuthStore();
  return { isOpen, reason, isReAuthenticating, lastEmail, openModal, closeModal, setReAuthenticating };
};

// Utility function to check if re-auth is needed (can be called outside React)
export const isReAuthModalOpen = () => useReAuthStore.getState().isOpen;

// Utility function to open re-auth modal (can be called outside React)
export const triggerReAuth = (reason: ReAuthReason, email?: string) => {
  useReAuthStore.getState().openModal(reason, email);
};
